import { httpsCallable } from 'firebase/functions';
import { getDoc } from 'firebase/firestore';
import { functions } from '../firebase';
import { orgIntegrationDoc } from './firestore';
import type {
  Integration,
  IntegrationProvider,
  RoutingMode,
  RoutingOp,
} from './types';

/**
 * Input shape for creating a routing rule. The server mints `id` and
 * snapshots `serviceName` — clients only supply the editable fields.
 */
export interface RoutingRuleInput {
  id?: string;
  field: string;
  op: RoutingOp;
  value: string | string[];
  serviceId: string;
}

interface CreateRequest {
  orgId: string;
  /** Required when routingMode === 'fixed'. */
  serviceId?: string;
  provider: IntegrationProvider;
  name: string;
  routingMode?: RoutingMode;
  routingRules?: RoutingRuleInput[];
  defaultServiceId?: string;
  /** Required for `sentry`; rejected for every other provider. */
  secret?: string;
}
interface CreateResponse {
  integrationId: string;
  secret: string;
}

export async function createIntegration(
  orgId: string,
  input: {
    provider: IntegrationProvider;
    name: string;
    routingMode?: RoutingMode;
    /** Required when routingMode === 'fixed'. */
    serviceId?: string;
    routingRules?: RoutingRuleInput[];
    defaultServiceId?: string;
    secret?: string;
  },
): Promise<{ integrationId: string; secret: string }> {
  const callable = httpsCallable<CreateRequest, CreateResponse>(functions, 'createIntegration');
  const result = await callable({ orgId, ...input });
  return result.data;
}

export async function updateIntegrationRouting(
  orgId: string,
  integrationId: string,
  input: { routingRules: RoutingRuleInput[]; defaultServiceId?: string },
): Promise<void> {
  const callable = httpsCallable(functions, 'updateIntegrationRouting');
  await callable({ orgId, integrationId, ...input });
}

export async function updateIntegrationSecret(
  orgId: string,
  integrationId: string,
  input: { secret?: string },
): Promise<{ secret: string }> {
  const callable = httpsCallable<
    { orgId: string; integrationId: string; secret?: string },
    { secret: string }
  >(functions, 'updateIntegrationSecret');
  const result = await callable({ orgId, integrationId, ...input });
  return result.data;
}

export async function deleteIntegration(orgId: string, integrationId: string): Promise<void> {
  const callable = httpsCallable(functions, 'deleteIntegration');
  await callable({ orgId, integrationId });
}

export async function getIntegration(
  orgId: string,
  integrationId: string,
): Promise<Integration | null> {
  const snap = await getDoc(orgIntegrationDoc(orgId, integrationId));
  if (!snap.exists()) return null;
  return readIntegration(snap.data() as IntegrationDocShape);
}

type IntegrationDocShape = Omit<Integration, 'createdAt' | 'lastUsedAt'> & {
  createdAt: number | { toMillis(): number };
  lastUsedAt: number | null | { toMillis(): number };
};

export function readIntegration(raw: IntegrationDocShape): Integration {
  return {
    ...raw,
    createdAt:
      typeof raw.createdAt === 'number' ? raw.createdAt : raw.createdAt.toMillis(),
    lastUsedAt:
      raw.lastUsedAt === null
        ? null
        : typeof raw.lastUsedAt === 'number'
          ? raw.lastUsedAt
          : raw.lastUsedAt.toMillis(),
  };
}

// ---------- Display helpers ----------

export const PROVIDER_LABELS: Record<IntegrationProvider, string> = {
  datadog: 'Datadog',
  newrelic: 'New Relic',
  sentry: 'Sentry',
  dynatrace: 'Dynatrace',
  pagerduty: 'PagerDuty',
  generic: 'Generic',
};

/**
 * Returns the deployed webhook URL for a given provider, with the
 * orgId + integrationId already in the query string. Admins copy this
 * into the provider's webhook configuration.
 *
 * Region + project are baked from the Firebase config the SDK was
 * initialised with so we don't have to maintain a separate constant.
 */
export function webhookUrl(
  provider: IntegrationProvider,
  orgId: string,
  integrationId: string,
): string {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const region = 'europe-west2';
  const fnName = providerFunctionName(provider);
  return `https://${region}-${projectId}.cloudfunctions.net/${fnName}?orgId=${encodeURIComponent(
    orgId,
  )}&integrationId=${encodeURIComponent(integrationId)}`;
}

function providerFunctionName(provider: IntegrationProvider): string {
  switch (provider) {
    case 'datadog':
      return 'datadogWebhook';
    case 'newrelic':
      return 'newRelicWebhook';
    case 'sentry':
      return 'sentryWebhook';
    case 'dynatrace':
      return 'dynatraceWebhook';
    case 'pagerduty':
      return 'pagerDutyWebhook';
    case 'generic':
      return 'genericWebhook';
  }
}
